import React from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import ErrorBoundary from "@default/js/error-boundary";

import { SolrProvider, URLSync, parseUrlState } from "@e-fork/solr";
import { festivalDayOffset } from "@default/js/lib/festival-day-offset";
import { mappedWeekday } from "@default/paragraphs/overview/components/main-controls/mapped-weekdays";

import SOLR_SEARCH_REST_CONFIG_VENUES from "./venues/solr_search_rest_config_venues";
import OverviewVenues from "./venues/overview-venues";
import SOLR_SEARCH_REST_CONFIG_PARTICIPANTS from "./participants/solr_search_rest_config_participants";
import OverviewActs from "./participants/overview-acts";
import OverviewSpeakers from "./participants/overview-speakers";
import SOLR_SEARCH_REST_CONFIG_APPEARANCES from "./timetable/solr_search_rest_config_appearances";
import OverviewTimetable from "./timetable/overview-timetable";
import OverviewNews from "./news/overview-news";
import SOLR_SEARCH_REST_CONFIG_NEWS from "./news/solr_search_rest_config_news";

// Wrap the Overview component with the OverviewProvider.
const ParagraphOverview = ({ content }) => {
  let config;
  const { filters, sorting, searchQuery } = parseUrlState();

  switch (content.entityBundle) {
    /**
     * Overview for venues.
     */
    case "overview_venues":
      config = { ...SOLR_SEARCH_REST_CONFIG_VENUES, presetFilters: [...SOLR_SEARCH_REST_CONFIG_VENUES.presetFilters] };
      if (content.fieldEdition?.id) {
        config.presetFilters.push({
          field: "edition_id",
          value: content.fieldEdition.id,
        });
      }
      return (
        <ErrorBoundary>
          <SolrProvider config={config} initialFilters={filters} initialSorting={sorting} initialSearchQuery={searchQuery}>
            <URLSync />
            <OverviewVenues content={content} />
          </SolrProvider>
        </ErrorBoundary>
      );

    /**
     * Overview for speakers.
     * Uses the participants-index.
     */
    case "overview_speakers":
      config = { ...SOLR_SEARCH_REST_CONFIG_PARTICIPANTS, presetFilters: [...SOLR_SEARCH_REST_CONFIG_PARTICIPANTS.presetFilters] };
      config.presetFilters.push({
        field: "participant_type",
        value: "speaker,moderator",
        operator: "OR"
      })
      if (content.fieldEdition?.id) {
        config.presetFilters.push({
          field: "edition_id",
          value: content.fieldEdition.id,
        });
      }
      if (content.fieldBranch) {
        config.presetFilters.push({
          field: "branch",
          value: content.fieldBranch.join(","),
        });
      }

      return (
        <ErrorBoundary>
          <SolrProvider config={config} initialFilters={filters} initialSorting={sorting} initialSearchQuery={searchQuery}>
            <URLSync />
            <OverviewSpeakers content={content} />
          </SolrProvider>
        </ErrorBoundary>
      );

    /**
     * Overview for acts.
     * Uses the participants-index.
     */
    case "overview_acts":
      config = { ...SOLR_SEARCH_REST_CONFIG_PARTICIPANTS, presetFilters: [...SOLR_SEARCH_REST_CONFIG_PARTICIPANTS.presetFilters] };
      if (content.fieldEdition?.id) {
        config.presetFilters.push({
          field: "edition_id",
          value: content.fieldEdition.id,
        });
      }
      if (content.fieldParticipantType) {
        config.presetFilters.push({
          field: "participant_type",
          value: content.fieldParticipantType,
        });
      }
      return (
        <ErrorBoundary>
          <SolrProvider config={config} initialFilters={filters} initialSorting={sorting} initialSearchQuery={searchQuery}>
            <URLSync />
            <OverviewActs content={content} />
          </SolrProvider>
        </ErrorBoundary>
      );

    /**
     * Overview for the timetable.
     */
    case "overview_timetable":
      config = { ...SOLR_SEARCH_REST_CONFIG_APPEARANCES, presetFilters: [...SOLR_SEARCH_REST_CONFIG_APPEARANCES.presetFilters] };
      if (content.fieldEdition?.id) {
        config.presetFilters.push({
          field: "edition_id",
          value: content.fieldEdition.id,
        });
      }
      if (content.fieldViewType) {
        config.presetFilters.push({
          field: "branch",
          value: content.fieldViewType.replace("_event", ""),
        });
      }
      const defaultSorting = config.sorting?.find(s => s.default);
      const timetableSorting = sorting || (defaultSorting ? `${defaultSorting.id}:${defaultSorting.order}` : undefined);
      const editionStart = content.fieldEdition?.date?.value;
      const editionEnd = content.fieldEdition?.date?.endValue;
      const isEditionHappening =
        !!editionStart &&
        !!editionEnd &&
        moment().isBetween(moment(editionStart), moment(editionEnd), undefined, "[]");
      const [festivalDayOffsetValue, festivalDayOffsetUnit] = festivalDayOffset;
      const currentFestivalWeekday = mappedWeekday(moment().add(festivalDayOffsetValue, festivalDayOffsetUnit));
      const initialFilters = {
        ...(isEditionHappening ? { appearance_weekday: [currentFestivalWeekday] } : {}),
        ...filters,
      }
      return (
        <ErrorBoundary>
          <SolrProvider config={config} initialFilters={initialFilters} initialSorting={timetableSorting} initialSearchQuery={searchQuery}>
            <URLSync />
            <OverviewTimetable content={content} />
          </SolrProvider>
        </ErrorBoundary>
      );

    /**
     * Overview for press releases and news.
     */
    case "overview_news":
      config = { ...SOLR_SEARCH_REST_CONFIG_NEWS, presetFilters: [...SOLR_SEARCH_REST_CONFIG_NEWS.presetFilters] };
      if (content.fieldNewsType) {
        config.presetFilters.push({
          field: "news_type",
          value: content.fieldNewsType,
        });
      }
      return (
        <ErrorBoundary>
          <SolrProvider config={config} initialFilters={filters} initialSorting={sorting} initialSearchQuery={searchQuery}>
            <URLSync />
            <OverviewNews content={content} />
          </SolrProvider>
        </ErrorBoundary>
      );
      
    case "default":
      return null;
  }
};

ParagraphOverview.propTypes = {
  content: PropTypes.shape({
    fieldContentType: PropTypes.oneOf([
      "news",
      "press",
      "event",
      "project",
      "person",
      "location",
      "act",
      "speaker",
      "timetable",
    ]),
  }),
};

export default ParagraphOverview;
